import {ContextView} from './context.models';
import {isCentralRole, Roles} from '../x-enums/roles';

/**
 * The SPA mirror of [`API.Core/Authorization/ContextViewMap.cs`](../../../../../../API.Core/Authorization/ContextViewMap.cs).
 *
 * The backend already draws the line this file needs: some central roles **are somewhere to go** (they open an
 * application — `ContextViewMap.For` answers a view for them), and the rest are **additive** — they widen what
 * you may do inside whichever view you chose, and are never a destination. `ContextViewMap.RolesForView` uses
 * that distinction when it mints a session; `AuthGuard` needs the same distinction when it decides whether a
 * session belongs in a narrower shell than the one it asked for. Stating the rule twice, differently, is how
 * the two drift apart — so it is stated once here, in the backend's own vocabulary.
 *
 * Kept beside {@link ContextView} rather than duplicating that enum: there is exactly one `ContextView` in the
 * SPA, and its ordinals are load-bearing on the wire.
 */

/**
 * The view `role` opens, or `null` when the role is additive (or not a destination at all, like the device-only
 * `Register`, which this vocabulary does not even name).
 *
 * Mirrors `ContextViewMap.For`. An unknown role — one a newer backend added that this build has never heard of
 * — answers `null`, which is the safe direction: it is then treated as additive, so it cannot silently switch
 * off a narrowing that protects a confined session. See {@link isSoleDestination}.
 */
export function contextViewFor(role: string): ContextView | null {
  switch (role) {
    case Roles.PlatformOwner:
      return ContextView.Platform;
    // One shell. Payroll, ShiftLead and Steuerberater are deliberately NOT their own applications: they see
    // the management screens with everything they hold no role for hidden.
    case Roles.CompanyOwner:
    case Roles.ShardAdmin:
    case Roles.Payroll:
    case Roles.ShiftLead:
    case Roles.Steuerberater:
      return ContextView.Management;
    case Roles.Employee:
      return ContextView.Employee;
    case Roles.Operator:
      return ContextView.Operator;
    default:
      // Roles.Auditor, Roles.SensitiveDataAccessor, every legacy endpoint-permission id, and anything unknown.
      return null;
  }
}

/** Whether holding `role` is a reason to offer somewhere to go. Mirrors `ContextViewMap.IsDestination`. */
export function isDestinationRole(role: string): boolean {
  return contextViewFor(role) !== null;
}

/**
 * Whether `role` is present in `held` **and is the only destination role there** — the one rule behind every
 * role-narrowing branch in {@link AuthGuard}.
 *
 * Those branches confine a session to a slice of the app ("a payroll accountant sees the employee list and
 * nothing else", "an operator sees the operator panel and nothing else"). That is only ever right while the
 * role is the *whole* reason the session exists. Each branch used to spell that out as a hand-maintained
 * exclusion list — `Payroll && !ShardAdmin && !PlatformOwner` — which was true when such a role could only be
 * held alone, and stopped being true the moment scoped grants (#261/#262) made every combination reachable. The
 * two lists were not even the same: the payroll one never excluded `CompanyOwner`, so a company owner who also
 * held `Payroll` was confined to the employee list in their own company. Counting destination roles states the
 * intent directly and cannot be half-updated.
 *
 * **Additive roles must not count.** An external accountant permitted unmasked data holds
 * `Payroll + SensitiveDataAccessor` — a realistic pairing, and still a payroll-only session. If additive roles
 * counted as "another destination", handing that person one extra privilege would silently switch the whole
 * confinement off and drop them into the admin app. That is the direction that matters here, and it is why the
 * predicate asks `isDestinationRole` rather than "holds anything else".
 *
 * Deliberately usable from **both sides of the guard**: over a session's held roles ("is this session nothing
 * but an operator?") and over a route's declared `data.roles` ("is this route for operators only?"). The
 * question is the same one, and legacy endpoint-permission ids mixed into either list are not destinations, so
 * they are ignored by construction.
 */
export function isSoleDestination(held: readonly string[] | null | undefined, role: string): boolean {
  if (!held?.includes(role)) return false;
  return held.every(candidate => candidate === role || !isDestinationRole(candidate));
}

/**
 * Whether a route's declared `data.roles` is gated **entirely on additive central roles** — a surface that
 * exists for a privilege rather than for a view. `/audit`, gated on `[Roles.Auditor]`, is the only one today.
 *
 * The counterpart to {@link isSoleDestination}, and deliberately a *different* question asked at a *different*
 * moment. `isSoleDestination` decides **whether** a narrowing branch applies, and additive roles must not
 * count there — an accountant handed one extra read privilege is still an accountant, and letting that switch
 * the confinement off is the one direction that would matter. This decides **what a session that is already
 * confined may still reach**, and there the same additive role has to mean something: it was granted
 * deliberately, the navbar offers its entry (`navbar.service.ts` gates the Audit group on `isAuditor`), and the
 * backend already admits it (`AuditController` names `Auditor` beside PlatformOwner/CompanyOwner, and no
 * baseline role at all).
 *
 * Narrow by construction, in both directions. The route's **whole** gate must be additive — one destination
 * role or one legacy endpoint-permission id in the list and the answer is `false`, so this can never open an
 * admin screen — and the caller must still check that the session actually **holds** those roles. An empty or
 * absent list is `false` rather than vacuously true: a route with no `roles` at all is the admin dashboard, not
 * an additive surface.
 */
export function isAdditiveOnlyGate(declared: readonly string[] | null | undefined): boolean {
  if (!declared?.length) return false;
  return declared.every(role => isCentralRole(role) && !isDestinationRole(role));
}
